/**
 * Video Capture Service
 *
 * Provides video recording, frame extraction at intervals, motion filtering,
 * settled frame detection, and deduplication for dice registration.
 *
 * Uses existing motion detection from camera.ts and quality assessment
 * from imageQuality.ts for frame analysis.
 */

import { captureFrame, detectMotion } from './camera';
import { createScopedReporter } from './errorReporter';
import { getImageDataFromVideo, assessImageQuality } from './imageQuality';

const SERVICE_TAG = '[VIDEO]';
const reporter = createScopedReporter('VIDEO');

/** Default video capture duration in milliseconds */
const DEFAULT_DURATION_MS = 3000;

/** Default interval between frame extractions in milliseconds */
const DEFAULT_FRAME_INTERVAL_MS = 100;

/** Minimum duration of settled state to consider dice at rest (ms) */
const DEFAULT_MIN_SETTLED_DURATION_MS = 500;

/** Motion threshold below which dice are considered settled */
const SETTLED_MOTION_THRESHOLD = 0.01;

/** Minimum quality score for a frame to be considered for deduplication */
const MIN_QUALITY_THRESHOLD = 0.3;

/** Pixel difference threshold for frame deduplication (0-1) */
const DEDUPLICATION_THRESHOLD = 0.02;

/**
 * Options for video capture sequence
 */
export interface VideoCaptureOptions {
  /** Total duration to capture in milliseconds (default: 3000) */
  durationMs?: number;
  /** Interval between frame extractions in milliseconds (default: 100) */
  frameIntervalMs?: number;
  /** Minimum duration of settled state in milliseconds (default: 500) */
  minSettledDurationMs?: number;
}

/**
 * A single extracted frame with metadata
 */
export interface ExtractedFrame {
  /** Base64 JPEG data URL */
  data: string;
  /** Frame capture timestamp (ms since epoch) */
  timestamp: number;
  /** Quality score 0-1 (higher is better) */
  quality: number;
  /** Whether motion was detected compared to previous frame */
  hasMotion: boolean;
  /** Whether dice are considered settled at this frame */
  isSettled: boolean;
}

/**
 * Result of a video capture sequence
 */
export interface VideoCaptureResult {
  /** All extracted frames */
  allFrames: ExtractedFrame[];
  /** Frames that passed the settled filter */
  settledFrames: ExtractedFrame[];
  /** Deduplicated settled frames (best unique frames) */
  uniqueFrames: ExtractedFrame[];
  /** Total capture duration in milliseconds */
  durationMs: number;
  /** Time when capture started */
  startTime: number;
  /** Time when capture ended */
  endTime: number;
}

/**
 * Internal state for tracking video capture progress
 */
interface CaptureState {
  isRecording: boolean;
  frames: ExtractedFrame[];
  startTime: number;
  lastFrameData: string | null;
  settledStartTime: number | null;
  intervalId: ReturnType<typeof setInterval> | null;
  resolveCapture: ((result: VideoCaptureResult) => void) | null;
}

/**
 * Video capture controller for managing recording state
 */
export class VideoCaptureController {
  private state: CaptureState = {
    isRecording: false,
    frames: [],
    startTime: 0,
    lastFrameData: null,
    settledStartTime: null,
    intervalId: null,
    resolveCapture: null
  };

  private videoElement: HTMLVideoElement | null = null;
  private options: Required<VideoCaptureOptions> = {
    durationMs: DEFAULT_DURATION_MS,
    frameIntervalMs: DEFAULT_FRAME_INTERVAL_MS,
    minSettledDurationMs: DEFAULT_MIN_SETTLED_DURATION_MS
  };

  /**
   * Check if currently recording
   */
  isRecording(): boolean {
    return this.state.isRecording;
  }

  /**
   * Get current frame count
   */
  getFrameCount(): number {
    return this.state.frames.length;
  }

  /**
   * Get current recording duration in milliseconds
   */
  getCurrentDuration(): number {
    if (!this.state.isRecording) {
      return 0;
    }
    return Date.now() - this.state.startTime;
  }

  /**
   * Start a video capture sequence
   *
   * @param videoElement - Video element to capture from
   * @param options - Capture options
   * @returns Promise that resolves when capture is complete or stopped
   */
  async startCapture(
    videoElement: HTMLVideoElement,
    options: VideoCaptureOptions = {}
  ): Promise<VideoCaptureResult> {
    if (this.state.isRecording) {
      console.warn(`${SERVICE_TAG} Capture already in progress, stopping previous capture`);
      await this.stopCapture();
    }

    this.videoElement = videoElement;
    this.options = {
      durationMs: options.durationMs ?? DEFAULT_DURATION_MS,
      frameIntervalMs: options.frameIntervalMs ?? DEFAULT_FRAME_INTERVAL_MS,
      minSettledDurationMs: options.minSettledDurationMs ?? DEFAULT_MIN_SETTLED_DURATION_MS
    };

    console.info(`${SERVICE_TAG} Starting video capture`, {
      durationMs: this.options.durationMs,
      frameIntervalMs: this.options.frameIntervalMs,
      minSettledDurationMs: this.options.minSettledDurationMs
    });

    this.state = {
      isRecording: true,
      frames: [],
      startTime: Date.now(),
      lastFrameData: null,
      settledStartTime: null,
      intervalId: null,
      resolveCapture: null
    };

    return new Promise((resolve) => {
      this.state.resolveCapture = resolve;

      // Start frame extraction interval
      this.state.intervalId = setInterval(() => {
        this.extractFrame();
      }, this.options.frameIntervalMs);

      // Extract first frame immediately
      this.extractFrame();

      // Set timeout for max duration
      setTimeout(() => {
        if (this.state.isRecording) {
          console.info(`${SERVICE_TAG} Capture duration reached, stopping`);
          this.stopCapture();
        }
      }, this.options.durationMs);
    });
  }

  /**
   * Stop the current capture and return results
   *
   * @returns Promise that resolves with capture results, or null if not recording
   */
  async stopCapture(): Promise<VideoCaptureResult | null> {
    if (!this.state.isRecording) {
      console.warn(`${SERVICE_TAG} No capture in progress`);
      return null;
    }

    // Clear interval
    if (this.state.intervalId) {
      clearInterval(this.state.intervalId);
      this.state.intervalId = null;
    }

    const endTime = Date.now();
    const durationMs = endTime - this.state.startTime;

    console.info(`${SERVICE_TAG} Video capture complete`, {
      totalFrames: this.state.frames.length,
      durationMs
    });

    // Filter settled frames
    const settledFrames = filterSettledFrames(
      this.state.frames,
      this.options.minSettledDurationMs
    );

    // Deduplicate frames
    const uniqueFrames = await deduplicateFrames(settledFrames, DEDUPLICATION_THRESHOLD);

    const result: VideoCaptureResult = {
      allFrames: [...this.state.frames],
      settledFrames,
      uniqueFrames,
      durationMs,
      startTime: this.state.startTime,
      endTime
    };

    console.info(`${SERVICE_TAG} Capture analysis complete`, {
      totalFrames: result.allFrames.length,
      settledFrames: result.settledFrames.length,
      uniqueFrames: result.uniqueFrames.length
    });

    // Reset state
    const resolveCapture = this.state.resolveCapture;
    this.state = {
      isRecording: false,
      frames: [],
      startTime: 0,
      lastFrameData: null,
      settledStartTime: null,
      intervalId: null,
      resolveCapture: null
    };
    this.videoElement = null;

    // Resolve the promise
    if (resolveCapture) {
      resolveCapture(result);
    }

    return result;
  }

  /**
   * Extract a single frame from the video
   */
  private async extractFrame(): Promise<void> {
    if (!this.videoElement || !this.state.isRecording) {
      return;
    }

    try {
      const timestamp = Date.now();
      const frameData = captureFrame(this.videoElement, 0.9);

      // Assess quality
      const imageData = getImageDataFromVideo(this.videoElement, 128);
      let quality = 0.5;
      if (imageData) {
        const assessment = assessImageQuality(imageData);
        quality = assessment.overallScore;
      } else {
        reporter.warning('Could not assess frame quality, using fallback', {
          context: { fallbackValue: 0.5, timestamp },
          showToast: false
        });
      }

      // Detect motion compared to previous frame
      let hasMotion = false;
      let motionMagnitude = 0;

      if (this.state.lastFrameData) {
        const motionResult = await detectMotion(this.state.lastFrameData, frameData);
        hasMotion = motionResult.hasMotion;
        motionMagnitude = motionResult.magnitude;
      }

      // Update settled state tracking
      const isSettledMotion = motionMagnitude < SETTLED_MOTION_THRESHOLD;
      let isSettled = false;

      if (isSettledMotion) {
        if (this.state.settledStartTime === null) {
          this.state.settledStartTime = timestamp;
        }
        const settledDuration = timestamp - this.state.settledStartTime;
        isSettled = settledDuration >= this.options.minSettledDurationMs;
      } else {
        this.state.settledStartTime = null;
      }

      const frame: ExtractedFrame = {
        data: frameData,
        timestamp,
        quality,
        hasMotion,
        isSettled
      };

      this.state.frames.push(frame);
      this.state.lastFrameData = frameData;
    } catch (error) {
      reporter.error('Frame extraction failed', {
        originalError: error instanceof Error ? error : undefined,
        context: { frameIndex: this.state.frames.length },
        showToast: false
      });
    }
  }
}

/**
 * Filter frames to only include settled frames (low motion for sustained period)
 *
 * @param frames - Array of extracted frames
 * @param minSettledDurationMs - Minimum duration of settled state
 * @returns Array of frames marked as settled
 */
export function filterSettledFrames(
  frames: ExtractedFrame[],
  minSettledDurationMs: number = DEFAULT_MIN_SETTLED_DURATION_MS
): ExtractedFrame[] {
  if (frames.length === 0) {
    console.info(`${SERVICE_TAG} No frames to filter`);
    return [];
  }

  // Frames are already marked with isSettled during capture
  const settled = frames.filter((frame) => frame.isSettled);

  console.info(`${SERVICE_TAG} Filtered settled frames`, {
    totalFrames: frames.length,
    settledFrames: settled.length,
    minSettledDurationMs
  });

  return settled;
}

/**
 * Deduplicate frames by comparing visual similarity
 * Keeps the highest quality frame from each group of similar frames
 *
 * @param frames - Array of frames to deduplicate
 * @param threshold - Pixel difference threshold (0-1, lower = stricter)
 * @returns Array of unique frames
 */
export async function deduplicateFrames(
  frames: ExtractedFrame[],
  threshold: number = DEDUPLICATION_THRESHOLD
): Promise<ExtractedFrame[]> {
  if (frames.length === 0) {
    console.info(`${SERVICE_TAG} No frames to deduplicate`);
    return [];
  }

  // Filter by minimum quality first
  const qualityFiltered = frames.filter((frame) => frame.quality >= MIN_QUALITY_THRESHOLD);

  if (qualityFiltered.length === 0) {
    reporter.warning('No frames passed quality threshold', {
      context: { threshold: MIN_QUALITY_THRESHOLD, totalFrames: frames.length },
      showToast: false
    });
    // Return the best quality frame if available
    if (frames.length > 0) {
      const best = frames.reduce((a, b) => (a.quality > b.quality ? a : b));
      console.info(`${SERVICE_TAG} Returning best available frame despite low quality`, {
        quality: best.quality
      });
      return [best];
    }
    return [];
  }

  const uniqueFrames: ExtractedFrame[] = [];

  for (const frame of qualityFiltered) {
    let isDuplicate = false;

    // Compare with existing unique frames
    for (const uniqueFrame of uniqueFrames) {
      const motionResult = await detectMotion(uniqueFrame.data, frame.data);

      // If frames are similar (low motion between them), they are duplicates
      if (motionResult.magnitude < threshold) {
        isDuplicate = true;

        // Keep the higher quality frame
        if (frame.quality > uniqueFrame.quality) {
          const index = uniqueFrames.indexOf(uniqueFrame);
          uniqueFrames[index] = frame;
          console.info(`${SERVICE_TAG} Replaced duplicate with higher quality frame`, {
            oldQuality: uniqueFrame.quality.toFixed(2),
            newQuality: frame.quality.toFixed(2)
          });
        }
        break;
      }
    }

    if (!isDuplicate) {
      uniqueFrames.push(frame);
    }
  }

  console.info(`${SERVICE_TAG} Deduplication complete`, {
    inputFrames: frames.length,
    qualityFiltered: qualityFiltered.length,
    uniqueFrames: uniqueFrames.length,
    threshold
  });

  return uniqueFrames;
}

/**
 * Convenience function to capture a video sequence
 * Creates a controller, runs capture, and returns results
 *
 * @param videoElement - Video element to capture from
 * @param options - Capture options
 * @returns Promise that resolves with capture results
 */
export async function captureVideoSequence(
  videoElement: HTMLVideoElement,
  options: VideoCaptureOptions = {}
): Promise<VideoCaptureResult> {
  const controller = new VideoCaptureController();
  return controller.startCapture(videoElement, options);
}
